import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { readSession } from '../_helpers/session.js';
import {
  COUNTRY_ELIGIBILITY_STATUS,
  COUNTRY_ELIGIBILITY_TIMEOUT_MS,
  UNKNOWN_ELIGIBILITY,
  countryEligibilityEndpoint,
  WAITLIST_FIELD_ERROR_KEYS,
  isAlreadyOnWaitlistError,
  isCountryEligibilityUnavailableError,
  isWaitlistLocationUnknownError,
  isWaitlistNotRequiredError,
  musicianWaitlistEndpoint,
  performerSignupOpen,
  readCountryEligibility,
  readWaitlistJoin,
  serializeWaitlistJoin,
  waitlistErrorField,
} from '../_helpers/countryEligibility.js';

const { IDLE, LOADING, READY, ERROR, UNAVAILABLE } = COUNTRY_ELIGIBILITY_STATUS;

/**
 * A failure as the component renders it: `{ text }` when the backend worded
 * the message, else `{ key }` of a catalog message to pass to `t()` — hooks
 * never phrase English themselves (ENG-380). Same convention as
 * `useUserRole`: fetchWrapper's `message` is the response body when there was
 * one and the English `statusText` otherwise, and a rejection without a
 * `status` is the browser's network error.
 */
const failure = (error, key) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  return message && error.status && message !== error.statusText ? { text: message } : { key };
};

/**
 * The account's country eligibility and its musician-waitlist entry
 * (ENG-375), for the register-performer page.
 *
 * Asked once per mount while a session is held; a signed-out visitor is left
 * `idle` with sign-up open, since the endpoint is authenticated and the
 * registration POST is the thing that will turn them away. The states that
 * close sign-up are the two the backend names (`waitlist`, `unsupported`) and
 * nothing else — a load that failed, one that did not answer inside
 * `COUNTRY_ELIGIBILITY_TIMEOUT_MS`, a deployment without the endpoint and a
 * state this build does not know all leave the form where it was, because a
 * rollout check that goes down must not become a rollout that locks everyone
 * out. `signupOpen` is the one flag the page reads for that.
 *
 * `join` posts the waitlist application (ENG-476) and reports its own
 * outcome. A 409 `waitlist_application_exists` is a success (the account is
 * on the list, which is what it asked for). A 409 `waitlist_not_required`
 * means this session may sign up after all, so the check is asked again and
 * the page follows its answer. Any other failure is handed back as
 * `joinError` — `{ key } | { text }`, with `field` when the backend named the
 * input it refused — and cleared by the next attempt, so a Try Again is the
 * same call again.
 */
export function useCountryEligibility({ enabled = readSession() } = {}) {
  const fetchWrapper = useFetchWrapper();
  const fetchWrapperRef = useRef(fetchWrapper);
  const [status, setStatus] = useState(enabled ? LOADING : IDLE);
  const [eligibility, setEligibility] = useState(UNKNOWN_ELIGIBILITY);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  // Bumped to ask the check again (a join the backend says is not required).
  const [reloads, setReloads] = useState(0);
  // Cleared on unmount so a join that lands afterwards does not set state.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus(IDLE);
      setEligibility(UNKNOWN_ELIGIBILITY);
      return undefined;
    }
    let cancelled = false;
    // The deadline and the answer race; the loser is dropped. An answer that
    // arrives after the page has given up and offered the form is not applied:
    // it would take the form away from someone already typing in it, which is
    // exactly what waiting for the check was meant to avoid.
    let settled = false;
    setStatus(LOADING);
    const timer = setTimeout(() => {
      if (cancelled || settled) return;
      settled = true;
      console.error(`Country eligibility check did not answer within ${COUNTRY_ELIGIBILITY_TIMEOUT_MS}ms`);
      setEligibility(UNKNOWN_ELIGIBILITY);
      setStatus(ERROR);
    }, COUNTRY_ELIGIBILITY_TIMEOUT_MS);
    const load = async () => {
      try {
        const response = await fetchWrapperRef.current.get(countryEligibilityEndpoint());
        if (cancelled || settled) return;
        settled = true;
        setEligibility(readCountryEligibility(response));
        setStatus(READY);
      } catch (err) {
        if (cancelled || settled) return;
        settled = true;
        // Either way the form stays: only a `ready` answer can close sign-up.
        setEligibility(UNKNOWN_ELIGIBILITY);
        if (isCountryEligibilityUnavailableError(err)) {
          setStatus(UNAVAILABLE);
        } else {
          console.error('Failed to load country eligibility:', err);
          setStatus(ERROR);
        }
      } finally {
        clearTimeout(timer);
      }
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, reloads]);

  const join = useCallback(async (application) => {
    setJoining(true);
    setJoinError(null);
    try {
      const response = await fetchWrapperRef.current.post(musicianWaitlistEndpoint(), serializeWaitlistJoin(application));
      if (!mountedRef.current) return true;
      setEligibility((current) => ({ ...current, waitlist: readWaitlistJoin(response) }));
      return true;
    } catch (err) {
      if (!mountedRef.current) return isAlreadyOnWaitlistError(err);
      if (isAlreadyOnWaitlistError(err)) {
        // Already on the list — the outcome the musician asked for, reached
        // by a second tab or a retry whose first attempt did land.
        setEligibility((current) => ({ ...current, waitlist: readWaitlistJoin(null) }));
        return true;
      }
      if (isWaitlistNotRequiredError(err)) {
        // The backend now places this session somewhere sign-up is open; the
        // check will say so and the page will offer the form.
        setReloads((count) => count + 1);
        return false;
      }
      if (isWaitlistLocationUnknownError(err)) {
        setJoinError({ key: 'registerBand.waitlist.errors.locationUnknown' });
        return false;
      }
      const field = waitlistErrorField(err);
      if (field) {
        // Our own wording for the field, not the backend's fragment ("is
        // required"), which reads as nothing without the field beside it.
        setJoinError({ key: WAITLIST_FIELD_ERROR_KEYS[field], field });
        return false;
      }
      console.error('Failed to join the musician waitlist:', err);
      setJoinError(failure(err, 'registerBand.waitlist.errors.joinFailed'));
      return false;
    } finally {
      if (mountedRef.current) setJoining(false);
    }
  }, []);

  return {
    status,
    country: eligibility.country,
    eligibility: eligibility.eligibility,
    waitlist: eligibility.waitlist,
    /** The form is offered unless the backend said this country is closed to performers. */
    signupOpen: performerSignupOpen(eligibility.eligibility),
    /** The answer is still outstanding; the page waits rather than flashing the form. */
    checking: status === LOADING,
    joining,
    joinError,
    join,
  };
}

export default useCountryEligibility;
